<template>
<div class="container">

	<widget class="form">
		<div style="max-width: 700px;margin: auto;">
		
			<h2>Employment Settings: {{$store.employee.first_name}} {{$store.employee.last_name}}</h2>
			<hr>

			<field 
				label="Employee Colour" 
				col-class="col-md-4" 
				v-model="settings.color_hex" 
				:params="{type: 'color'}"
				v-on:change="save">
			</field>

			<field 
				label="Hire Date" 
				col-class="col-md-4" 
				v-model="settings.hire_date" 
				:params="{type: 'date'}"
				v-on:change="save">
			</field>

			<field 
				label="Classification" 
				col-class="col-md-4" 
				v-model="settings.classification_id" 
				:params="{type: 'select', options:classificationOptions, grouped:true}" 
				:readonly="employeeIsEx"
				v-on:change="save">
			</field>

			<field
				label="Employee #"
				col-class="col-md-4"
				v-model="settings.employee_num"
				:readonly="employeeIsEx"
				v-on:change="save">
			</field>

			<field label="Is Salaried Employee" 
				col-class="col-md-4" 
				v-model="settings.is_salary" 
				:params="{type:'boolean'}"
				:readonly="employeeIsEx"
				v-on:change="save">

				<small class="text-muted form-text mt-1">This will turn off the Time Log Review process for this employee</small>
			</field>

			<field label="Is Service Crew Member" 
				col-class="col-md-4" 
				v-model="settings.is_service_tech" 
				:readonly="employeeIsEx"
				:params="{type:'boolean'}"
				v-on:change="save">

				<small class="text-muted form-text mt-1 mb-3">This will toggle whether this employee will have limited access to the Service module. They will be able to:

					<ul>
						<li>Create Emergency Service Work Orders</li>
						<li>Save data completed on the Service Job Worksheet Tab</li>
					</ul>

					To assign greater permissions, they should be given full <router-link :to="'/employee/'+$route.params.id+'/permissions'">Service Module Permissions</router-link>
				</small>
			</field>

			<field label="Num. Annual Vacation Days"
				col-class="col-md-4"
				v-model="settings.num_annual_vacation_days"
				:params="{type:'number',min:0,step:1}"
				:readonly="employeeIsEx"
				v-on:change="debounce('save',null,1000)">
			</field>

			<field label="Set reply-to for outgoing emails to the sending user's email"
				col-class="col-md-4"
				v-model="settings.reply_to_account_email"
				:params="{type:'boolean'}"
				:readonly="employeeIsEx"
				v-on:change="save">
			</field>

			<field v-if="qboIsActive"
				label="Map to QBO Employee"
				col-class="col-md-4"
				v-model="settings.qbo_employee_id"
				:params="{type:'select',options:qboEmployeeOptions}"
				v-on:change="save">
			</field>

		</div>
	</widget>

	<widget v-if="!employeeIsEx" class="form">

		<div class="row">
			<div class="col-lg-3 col-md-4 col-sm-5">
				<button v-on:click="confirmModalVisible = true" class="btn btn-outline-danger" style="width: 100%;"><i class="fa fa-trash"></i> Terminate Employee</button>
			</div>
			<div class="col-lg-9 col-md-8 col-sm-7">
				<p>This will release an employee from any association with your company within {{$store.appName}}. The employee will no longer have any access to your company data, beyond his/her personal information.</p>
			</div>
		</div>
	</widget>

	<modal-dialog
		:modal-visible="confirmModalVisible"
		title="Terminate Employee"
		confirm-button-text="Yes, Terminate"
		v-on:confirm="terminateEmployee"
		v-on:closeModal="confirmModalVisible = false">
		Are you absolutely sure you want to terminate this employee?
	</modal-dialog>

</div>
</template>

<script>

import VueForm from "@/components/VueForm"
import Field from "@/components/Field"
import ModalDialog from "@/components/ModalDialog"
import Widget from "@/components/Widget"

export default {

	name: 'EmploymentSettings',
	components: {
		Field,
		VueForm,
		ModalDialog,
		Widget
	},
	data() {
		return {
			classificationOptions: [],
			statusWorking: false,
			readonly: false,
			settings: {},
			confirmModalVisible:false,
			qboEmployees: []
		}
	},
	watch: {
		qboIsActive() {
			this.fetchQboEmployees()
		}
	},
	computed: {
		employeeIsEx() {
			return !('UserPermission' in this.$store.employee && this.$store.employee.UserPermission.length > 0 && this.$store.employee.UserPermission[0].is_inactive == 0)
		},
		qboEmployeeOptions() {
			return this.qboEmployees.map(emp => {
				return {
					text: emp.DisplayName,
					value: emp.Id
				}
			})
		}
	},
	methods: {
		save() {
			let vm = this

			this.$nextTick(() => {
				let payload = {
					user_id: vm.$store.employee.id,
					classification_id: vm.settings.classification_id,
					employee_num: vm.settings.employee_num,
					is_service_tech: vm.settings.is_service_tech,
					is_salary: vm.settings.is_salary,
					num_annual_vacation_days: vm.settings.num_annual_vacation_days,
					qbo_employee_id: vm.settings.qbo_employee_id,
					color_hex: vm.settings.color_hex,
					reply_to_account_email: vm.settings.reply_to_account_email,
					hire_date: vm.settings.hire_date
				}

				vm.saveRecord("EmployeeSetting",payload)
			})
		},
		terminateEmployee() {

			let vm = this

			this.postData("User/terminate-user",{
				id: this.$store.employee.id,
				tenant_id: this.$store.userDetails.tenant_id
			}).then(response => {
				vm.confirmModalVisible = false
				vm.alertSuccess(this.$store.employee.first_name + " " + this.$store.employee.last_name + " has been terminated from the company")
				vm.$router.push("/employee/"+this.$store.employee.slug+"/profile")
			})
			
		},
		fetchQboEmployees() {
			let vm = this

			return new Promise((resolve,reject) => {

				this.getJSON("QuickbooksConnection/get-resource",{resource:"Employee"}).then(response => {

					if (response.success) 
						vm.qboEmployees = response.data

					resolve(response)
				})

			})
		},
		fetchData() {
			let vm = this

			let promises = [
				this.getJSON("EmployeeClassification/get"),
				this.getJSON("User/find/"+this.$route.params.id,{include: ["EmployeeSetting"]}),
				this.getJSON("Union/get")
			]

			if (this.qboIsActive)
				promises.push(this.fetchQboEmployees())

			Promise.all(promises).then(function(responses){
				vm.spinner(false)

				//classification list
				//vm.$store.employeeClassifications = responses[0].data

				let groupedClassifications = []
				let unions = responses[2].data

				unions.push({
					union_name: "Non-Unionized",
					id: 0
				})

				unions.forEach(union => {
					groupedClassifications.push({
						groupLabel: union.union_name,
						groupValues: responses[0].data
							.filter(classification => {
								if (classification.union_id == union.id || (classification.union_id == null && union.id == 0))
									return true
							})
							.map(classification => {
								return {
									text: classification.title,
									value: classification.id
								}
							})
					})
				})
				
				vm.classificationOptions = groupedClassifications

				//user settings
				if (responses[1].data.EmployeeSetting.length > 0)
					vm.settings = responses[1].data.EmployeeSetting[0]
				else
					vm.settings.classification_id = null

				if (responses.length == 4)
					vm.qboEmployees = responses[3].data
			})

		}
	},
	created() {
		this.spinner(true)
		this.fetchData()


	}

}
</script>